import React, { useState } from 'react';
import { ArrowUpRight, MapPin, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { IMAGES, EVENTS, MORE_EVENTS, CONCERTS, TICKET_TIERS } from '../mock';
import { postEnquiry } from '../api';
import { logError } from '../lib/logger';
import Reveal from './Reveal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const TICKET_SIZES = {
  full: { box: 'gap-2.5 px-6 py-3 text-[10px] border-white/30', icon: 12 },
  compact: { box: 'gap-2 px-5 py-2.5 text-[9px] border-white/25', icon: 11 },
};

const TicketButton = ({ event, onTickets, size = 'full', testId }) => {
  const s = TICKET_SIZES[size];
  const base = `group/btn inline-flex items-center border tracking-[0.25em] ${s.box}`;
  const liveClass = `${base} text-white transition-colors duration-300 hover:border-gold hover:text-gold`;
  const icon = (
    <ArrowUpRight
      size={s.icon}
      className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5"
    />
  );

  if (event.ticketUrl) {
    return (
      <a href={event.ticketUrl} target="_blank" rel="noopener noreferrer" data-testid={testId} className={liveClass}>
        GET TICKETS
        {icon}
      </a>
    );
  }
  if (event.ticketsSoon) {
    return (
      <button
        disabled
        data-testid={testId}
        title="Tickets coming soon"
        className={`${base} cursor-not-allowed !border-white/15 text-neutral-500`}
      >
        GET TICKETS
        <ArrowUpRight size={s.icon} />
      </button>
    );
  }
  return (
    <button onClick={() => onTickets(event)} data-testid={testId} className={liveClass}>
      GET TICKETS
      {icon}
    </button>
  );
};

const EventRow = ({ event, onTickets, delay = 0 }) => (
  <Reveal delay={delay}>
    <div className="group flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-white/10 py-8 transition-colors duration-300 hover:bg-white/[0.02]">
      <div className="min-w-[72px]">
        <p className="font-serif text-4xl font-light leading-none text-white md:text-5xl">{event.day}</p>
        <p className="mt-2.5 text-[9px] tracking-[0.3em] text-neutral-500">{event.month}</p>
      </div>
      <div className="min-w-[180px] flex-1">
        <h3 className="font-serif text-2xl font-light tracking-[0.03em] text-white transition-colors duration-300 group-hover:text-gold md:text-[1.7rem]">
          {event.title}
        </h3>
        <p className="mt-1.5 text-xs tracking-[0.05em] text-neutral-500">{event.venue}</p>
      </div>
      <TicketButton event={event} onTickets={onTickets} testId={`home-event-tickets-${event.id}`} />
    </div>
  </Reveal>
);

const LiveIntro = ({ onViewAll }) => (
  <div className="lg:col-span-4">
    <Reveal>
      <p className="mb-5 text-[10px] uppercase tracking-[0.5em] text-gold">Live</p>
    </Reveal>
    <Reveal delay={120}>
      <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-[0.03em] text-white md:text-6xl">
        UPCOMING
        <br />
        TOURS &
        <br />
        EVENTS
      </h2>
    </Reveal>
    <Reveal delay={240}>
      <p className="mt-8 max-w-sm text-base leading-relaxed text-neutral-400">
        Be a part of the experience. Catch Kanwal live at upcoming concerts and special appearances around the world.
      </p>
    </Reveal>
    <Reveal delay={340}>
      <button
        onClick={onViewAll}
        className="group mt-10 inline-flex items-center gap-3 bg-gold px-7 py-3.5 text-[11px] tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-gold-light"
      >
        VIEW ALL EVENTS
        <ArrowUpRight
          size={13}
          className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </button>
    </Reveal>
  </div>
);

const DialogEventRow = ({ event, onTickets }) => (
  <div className="group flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-white/5 py-5">
    <div className="min-w-[60px]">
      <p className="font-serif text-3xl font-light leading-none text-white">{event.day}</p>
      <p className="mt-1.5 text-[8px] tracking-[0.3em] text-neutral-500">{event.month}</p>
    </div>
    <div className="min-w-[160px] flex-1">
      <p className="font-serif text-lg tracking-[0.04em] text-white transition-colors duration-300 group-hover:text-gold">
        {event.title}
      </p>
      <p className="mt-1 text-[11px] text-neutral-500">{event.venue}</p>
    </div>
    <TicketButton event={event} onTickets={onTickets} size="compact" />
  </div>
);

const PastHighlight = ({ concert }) => (
  <div className="flex items-start justify-between gap-4 border-t border-white/5 py-4">
    <div>
      <p className="font-serif text-lg tracking-[0.05em] text-neutral-300">{concert.city}</p>
      <p className="mt-1 flex items-center gap-1.5 text-[11px] tracking-[0.08em] text-neutral-500">
        <MapPin size={11} className="text-gold" /> {concert.venue}
      </p>
    </div>
    <p className="flex shrink-0 items-center gap-1.5 pt-1 text-[10px] tracking-[0.12em] text-neutral-500">
      <CalendarDays size={11} className="text-gold" /> {concert.date}
    </p>
  </div>
);

const AllEventsDialog = ({ open, onOpenChange, onTickets }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[85vh] overflow-y-auto border-white/10 bg-ink text-neutral-200 sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle className="font-serif text-3xl font-light tracking-[0.06em] text-white">ALL EVENTS</DialogTitle>
        <DialogDescription className="text-[10px] uppercase tracking-[0.4em] text-gold">
          Tour dates & special appearances
        </DialogDescription>
      </DialogHeader>
      <div className="pt-2">
        {[...EVENTS, ...MORE_EVENTS].map((event) => (
          <DialogEventRow key={event.id} event={event} onTickets={onTickets} />
        ))}
        <p className="mb-2 mt-10 text-[10px] uppercase tracking-[0.4em] text-gold">Past highlights</p>
        {CONCERTS.map((c) => (
          <PastHighlight key={c.city} concert={c} />
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

const TierButton = ({ tier, onSelect }) => (
  <button
    onClick={() => onSelect(tier)}
    className="group flex w-full items-center justify-between border border-white/15 px-5 py-4 text-left transition-colors duration-300 hover:border-gold"
  >
    <span>
      <span className="block text-xs tracking-[0.2em] text-white transition-colors duration-300 group-hover:text-gold">
        {tier.name}
      </span>
      <span className="mt-1 block text-[10px] tracking-[0.1em] text-neutral-500">{tier.price}</span>
    </span>
    <ArrowUpRight size={14} className="text-neutral-500 transition-colors duration-300 group-hover:text-gold" />
  </button>
);

const TicketDialog = ({ event, onClose, onReserve }) => (
  <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="border-white/10 bg-ink text-neutral-200 sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl font-light tracking-[0.05em] text-white">{event?.title}</DialogTitle>
        <DialogDescription className="text-[10px] uppercase tracking-[0.35em] text-gold">
          {event ? `${event.day} ${event.month} · ${event.venue}` : ''}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 pt-2">
        {TICKET_TIERS.map((tier) => (
          <TierButton key={tier.id} tier={tier} onSelect={onReserve} />
        ))}
      </div>
      <p className="pt-1 text-[10px] leading-relaxed tracking-[0.05em] text-neutral-600">
        Preview build — reservations are stored locally and will connect to a ticketing partner in the full version.
      </p>
    </DialogContent>
  </Dialog>
);

const Live = () => {
  const [allOpen, setAllOpen] = useState(false);
  const [ticketEvent, setTicketEvent] = useState(null);

  const openTicketsFromAll = (event) => {
    setAllOpen(false);
    setTicketEvent(event);
  };

  const reserveTier = async (tier) => {
    try {
      await postEnquiry('tickets', {
        Event: ticketEvent.title,
        Venue: ticketEvent.venue,
        Date: `${ticketEvent.day} ${ticketEvent.month}`,
        Tier: tier.name,
        Price: tier.price,
      });
    } catch (err) {
      logError('Ticket reservation failed:', err);
      toast.error('Could not reserve tickets', {
        description: 'Please check your connection and try again in a moment.',
      });
      return;
    }
    setTicketEvent(null);
    toast.success('Tickets reserved', {
      description: `${tier.name} · ${ticketEvent.title} — a confirmation email will follow.`,
    });
  };

  return (
    <section id="live" className="relative overflow-hidden bg-ink py-16 md:py-20">
      <div className="absolute inset-0">
        <img
          src={IMAGES.live}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="h-full w-full object-cover object-right opacity-[0.13]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-transparent to-ink" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
          <LiveIntro onViewAll={() => setAllOpen(true)} />
          <div className="lg:col-span-8">
            {EVENTS.map((event, i) => (
              <EventRow key={event.id} event={event} onTickets={setTicketEvent} delay={i * 110} />
            ))}
            <div className="border-t border-white/10" />
          </div>
        </div>
      </div>

      <AllEventsDialog open={allOpen} onOpenChange={setAllOpen} onTickets={openTicketsFromAll} />
      <TicketDialog event={ticketEvent} onClose={() => setTicketEvent(null)} onReserve={reserveTier} />
    </section>
  );
};

export default Live;
